import React from 'react';
import { CheckIcon, XIcon, PiggyBankIcon } from 'lucide-react';
import { savingsRows, savingsTotals, SavingsRow } from '../../data/savings';
import { SectionHeading } from '../SectionHeading';
import { TextButton } from '../TextButton';
import { smsMessages } from '../../utils/sms';
interface PanelProps {
  name: string;
  note: string;
  rows: SavingsRow[];
  side: 'hydro' | 'salt';
  total: {
    label: string;
    value: string;
  };
}
function Panel({
  name,
  note,
  rows,
  side,
  total
}: PanelProps) {
  const isHydro = side === 'hydro';
  return <div className={isHydro ? 'bg-ink-950 p-7 sm:p-9' : 'bg-paper p-7 sm:p-9'} data-id="element-124" data-mp="buxfowew74xi">
      <div className="flex flex-col gap-1" data-id="element-125" data-mp="gsfrcukany97">
        <h3 className={`font-display text-xl font-semibold tracking-tight ${isHydro ? 'text-white' : 'text-ink-950'}`} data-id="element-126" data-mp="grnwxn2fo6ki">
          {name}
        </h3>
        <p className={`text-[13px] ${isHydro ? 'text-aqua-300' : 'text-ink-600'}`} data-id="element-127" data-mp="3k83dhlgz194h">
          {note}
        </p>
      </div>

      <dl className="mt-7" data-id="element-128" data-mp="2g15q740i7cu5">
        {rows.map(row => {
        const cell = row[side];
        return <div key={row.label} className={`flex min-h-[3.5rem] items-center justify-between gap-4 border-t py-3 ${isHydro ? 'border-white/10' : 'border-ink-950/10'}`} data-id="element-129" data-mp="2b1u7vqkhnzag">
              <dt className={`text-sm leading-snug ${isHydro ? 'text-white/70' : 'text-ink-700'}`} data-id="element-130" data-mp="229s7fsaf6j06">
                {row.label}
              </dt>
              <dd className={`flex shrink-0 items-center gap-1.5 text-sm font-medium ${cell.good ? isHydro ? 'text-aqua-300' : 'text-aqua-700' : isHydro ? 'text-white/45' : 'text-ink-400'}`} data-id="element-131" data-mp="1sdxh0azyfjmu">
                {cell.good ? <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-132" data-mp="1b2sbhc8xa4w7" /> : <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-133" data-mp="1ft8ncrx3higg" />}
                {cell.value}
              </dd>
            </div>;
      })}
      </dl>

      <div className={`mt-7 border-t pt-7 ${isHydro ? 'border-white/10' : 'border-ink-950/10'}`} data-id="element-134" data-mp="11zr5rcnyyft2">
        <p className={`text-xs uppercase tracking-[0.16em] ${isHydro ? 'text-white/45' : 'text-ink-400'}`} data-id="element-135" data-mp="1tt9qyf4qv3zm">
          {total.label}
        </p>
        <p className={`mt-2 font-display text-[34px] font-semibold tracking-tight ${isHydro ? 'text-white' : 'text-ink-950'}`} data-id="element-136" data-mp="1ksbtygsq3wqj">
          {total.value}
        </p>
      </div>
    </div>;
}
export function SavingsCard() {
  return <section className="border-t border-ink-950/8 bg-paper py-20 lg:py-28" data-id="element-137" data-mp="9f9e5nyg7b7f">
      <div className="mx-auto max-w-shell px-5 sm:px-8" data-id="element-138" data-mp="edufjqpfs0xg">
        <SectionHeading eyebrow="Cost of ownership" title="Chemical-free costs less every single year." lede={'A saltwater pool still buys chlorine, stabilizer and generator cells forever. Hydrosprings doesn\u2019t \u2014 and the difference shows up on your yearly bill.'} data-id="element-139" data-mp="5upr4csyk4e5" />

        <div className="mt-12 overflow-hidden rounded-3xl ring-1 ring-ink-950/10 shadow-lift" data-id="element-140" data-mp="avn48az4288s">
          <div className="grid lg:grid-cols-2" data-id="element-141" data-mp="3jax6bj303dgk">
            <Panel side="hydro" name="Hydrosprings system" note={'Acid changes and a probe swap. That\u2019s the whole cost.'} rows={savingsRows} total={savingsTotals.hydro} data-id="element-142" data-mp="3eb2gzjyvivxm" />
            <Panel side="salt" name="Saltwater system" note="Chlorine generator, chemicals, stabilizer, cell replacement." rows={savingsRows} total={savingsTotals.salt} data-id="element-143" data-mp="3o7nme6et291z" />
          </div>

          <div className="flex flex-col gap-5 border-t border-ink-950/10 bg-aqua-50 px-7 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-9" data-id="element-144" data-mp="1ytz2sqkup758">
            <p className="flex items-start gap-3 text-[15px] leading-relaxed text-ink-800" data-id="element-145" data-mp="23q66sdtos8kj">
              <PiggyBankIcon className="mt-0.5 h-5 w-5 shrink-0 text-aqua-700" aria-hidden="true" data-id="element-146" data-mp="27rrhbsir0ark" />
              <span data-id="element-147" data-mp="2hk73l212vli9">
                You keep{' '}
                <strong className="font-semibold text-ink-950" data-id="element-148" data-mp="2hlrvskzx6scy">
                  {savingsTotals.delta}
                </strong>{' '}
                every year &mdash; and the water is cleaner while you do.
              </span>
            </p>
            <TextButton message={smsMessages.quote} variant="ink" size="sm" className="shrink-0" data-id="element-149" data-mp="2vjykpbmw5i0h">
              Text us
            </TextButton>
          </div>
        </div>

        <p className="mt-4 text-xs text-ink-400" data-id="element-150" data-mp="115pa526yfvqs">
          Figures are national averages for annual operating cost and will vary
          by pool size, region and water conditions.
        </p>
      </div>
    </section>;
}